/**
 * Copies the given text to the clipboard.
 *
 * @param text The text to copy.
 * @returns A Promise that resolves with void if the copy was successful, or rejects with an error message if it failed.
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  if (!text) {
    return Promise.reject('No text provided to copy.');
  }

  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(
        'Failed to copy text using navigator.clipboard: ' +
          (err instanceof Error ? err.message : String(err))
      );
    }
  } else if (
    document.queryCommandSupported &&
    document.queryCommandSupported('copy')
  ) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in MS Edge.
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const successful = document.execCommand('copy');
      if (!successful) {
        return Promise.reject(
          "Failed to copy text using document.execCommand('copy')."
        );
      }
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(
        "Failed to copy text using document.execCommand('copy'): " +
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      document.body.removeChild(textarea);
    }
  } else {
    return Promise.reject(
      'Copy to clipboard is not supported in this browser.'
    );
  }
}
